// src/stores/session.js
import { defineStore } from "pinia";
import { scheduleRefresh, stopRefreshScheduler } from "@/services/api_client";
import chatApi from "@/services/chat_api";
import mentorApi from "@/services/mentor_api";
import debugApi from "@/services/debug_api";
import registrationApi from "@/services/registration_api";
import { useThemeStore } from "@/stores/theme";

let _fetchChatHistoryPromise = null;

export const useSessionStore = defineStore("session", {
  persist: true, // or {options}
  state: () => ({
    username: "",
    roles: {},
    firstname: "",
    lastname: "",

    chatbotName: "Olivia",

    sendSms: false,
    sendEmail: true,
    enableSessionManipulation: false,
    profilePhoto: "",
    phoneNumber: "",
    chatHistory: [],
    currentSessionId: "",
    currentChatHistory: [],
    newSessionNeeded: false,
    // Nightly maintenance window, as reported by /session/list. Null means the
    // feature is off or the participant is exempt (demo accounts), and every
    // consumer treats null as "nothing is happening" rather than checking a
    // flag. It is also what an older backend returns, so the page degrades to
    // its previous behaviour rather than erroring.
    //
    // Holds ABSOLUTE instants, never clock times: the window is defined in
    // America/Chicago and the browser is not, so the page renders these into
    // the participant's own zone and needs to know nothing about zones itself.
    maintenance: null,
    selectedChatHistory: [],
    isViewingCurrentChat: true,
    selectedSummary: "",

    mentees: [],
    pastSessionsWithSummaries: [],
    pastSessionsWithSummariesAndSuggestions: [],

    debugLogs: [],
    debugEnabled: false,
    debugPanelOpen: false,

    phoneVerified: null, // null = unknown, true/false after first check

    groupName: null, // e.g. military0
    schoolYear: null, // e.g. 1, 2, 3, 4
    profileLoaded: false,

    surveyLink: "",
  }),
  getters: {
    rolesIncludes: (state) => (role) => !!state.roles[role],
    token: () => sessionStorage.getItem("authToken"),
    refreshToken: () => sessionStorage.getItem("refreshToken"),
    getProfilePhoto: (state) => state.profilePhoto || sessionStorage.getItem("profilePhoto") || "",
    isAuthenticated: () => !!sessionStorage.getItem("authToken"),
    isPhoneVerified: (state) => state.phoneVerified === true,
    isMilitaryUndecided: (state) => state.groupName === "military0",
    isSmsOn: (state) => state.sendSms,
    isEmailOn: (state) => state.sendEmail,
    getPhoneNumber: (state) => state.phoneNumber,
  },
  actions: {
    login(
      username, 
      roles, 
      firstname, 
      lastname, 
      token, 
      refreshToken, 
      groupName,
      sendSms = false,
      sendEmail = true,
      profilePhoto = null,
      phoneNumber = null,
      enableSessionManipulation = false
    ) {
      // TODO: validate roles as string/list/hash
      this.$patch({
        username: username,
        roles: roles,
        firstname: firstname,
        lastname: lastname,
        groupName: groupName,
        profileLoaded: true,
        sendSms: sendSms,
        sendEmail: sendEmail,
        enableSessionManipulation: enableSessionManipulation,
        profilePhoto: profilePhoto || "",
        phoneNumber: phoneNumber
      });
      sessionStorage.setItem("authToken", token);
      sessionStorage.setItem("refreshToken", refreshToken);
      if (profilePhoto) {
        sessionStorage.setItem("profilePhoto", profilePhoto);
      }

      console.log("username", this.username);
      console.log("roles", this.roles);
      console.log("firstname", this.firstname);
      console.log("lastname", this.lastname);
      console.log("sendSms", this.sendSms);
      console.log("sendEmail", this.sendEmail);
      scheduleRefresh(); // handle refresh scheduler in session
      return this.checkVerificationStatus();
    },
    async checkVerificationStatus() {
      try {
        const isVerified = await registrationApi.checkVerificationStatus();
        this.phoneVerified = !!isVerified;
        return this.phoneVerified;

      } catch (error) {
        console.error("Error checking verification status:", error);
        this.phoneVerified = false;
        return false;
      }
    },
    markPhoneVerified() {
      this.phoneVerified = true;
    },
    logout() {
      this.$reset();
      sessionStorage.removeItem("authToken");
      sessionStorage.removeItem("refreshToken");
      sessionStorage.removeItem("profilePhoto");
      stopRefreshScheduler(); // handle refresh scheduler in session

      try {
        const theme = useThemeStore();
        theme.initFromHostname();
        theme.applyCssVarsToRoot();
      } catch (err) {
        console.warn("Theme reset failed on logout:", err);
      }
    },
    setProfilePhoto(photo) {
      const value = photo || "";
      this.profilePhoto = value;
      if (value) {
        sessionStorage.setItem("profilePhoto", value);
      } else {
        sessionStorage.removeItem("profilePhoto");
      }
    },
    setDebugLogs(logs) {
      this.debugLogs = logs;
    },
    addDebugLogs(newLogs) {
      this.debugLogs = [...this.debugLogs, ...newLogs];
    },
    setDebugPanelOpen(val) {
      this.debugPanelOpen = val;
    },
    /*
    async createNewSession() {
      try {
        const newSession = await chatApi.createNewSession(this.token);
        this.currentSessionId = newSession.id;
        //this.currentChatHistory = [];
        //this.chatHistory = [];
        //this.newSessionNeeded = newSession.new_session_needed;
      } catch (error) {
        console.error("Error creating new session:", error);
      }
    },
    */
    /**
     * Extracts the first element with 'new_session_needed' attribute from chatHistory
     * and returns both that element and the remaining history
     * @param {Array} chatHistory - The chat history array to process
     * @returns {Object} Object containing { newSessionInfo, remainingHistory }
     */
    extractNewSessionInfo(chatHistory) {
      if (
        !chatHistory ||
        !Array.isArray(chatHistory) ||
        chatHistory.length === 0
      ) {
        return { newSessionInfo: null, remainingHistory: [] };
      }

      // Find the index of the first element with new_session_needed attribute
      const newSessionIndex = chatHistory.findIndex(
        (session) => "new_session_needed" in session
      );

      if (newSessionIndex === -1) {
        // No element with new_session_needed found
        return { newSessionInfo: null, remainingHistory: [...chatHistory] };
      }

      // Extract the element
      const newSessionInfo = chatHistory[newSessionIndex];

      // Create a new array without the extracted element
      const remainingHistory = [
        ...chatHistory.slice(0, newSessionIndex),
        ...chatHistory.slice(newSessionIndex + 1),
      ];

      newSessionInfo.survey_link = typeof newSessionInfo.survey_link === "string"
        ? newSessionInfo.survey_link
        : "";

      return { newSessionInfo, remainingHistory };
    },
    async fetchChatHistory(needNewSession = false) {
      if (!needNewSession && _fetchChatHistoryPromise) return _fetchChatHistoryPromise;
      const promise = this._doFetchChatHistory(needNewSession);
      if (!needNewSession) {
        _fetchChatHistoryPromise = promise;
        promise.finally(() => { _fetchChatHistoryPromise = null; });
      }
      return promise;
    },
    async _doFetchChatHistory(needNewSession = false) {
      try {
        const chatHistory = await chatApi.getChatHistory(needNewSession);
        const { newSessionInfo, remainingHistory } =
          this.extractNewSessionInfo(chatHistory);

        let currentSessionId = null;
        let currentChatHistory = [];
        let previousChatHistory = [];
        let newSessionNeeded = false;
        let surveyLink = "";
        let maintenance = null;

        if (newSessionInfo) {
          newSessionNeeded = newSessionInfo.new_session_needed;
          // The backend can only tag survey_link with a session that already
          // exists. While new_session_needed is true, no session has been
          // created yet for the upcoming period, so any survey_link here
          // would still name the participant's just-finished session -- not
          // the one "Begin a new session!" is about to create. Trust it only
          // once that creation has happened and new_session_needed is false.
          surveyLink = newSessionNeeded ? "" : (newSessionInfo.survey_link || "");
          // Rides along on a response the page already fetches, which is why
          // the window needs no endpoint of its own and no polling.
          maintenance = newSessionInfo.maintenance || null;
        }

        // new_session_needed flips at THIS participant's own period boundary,
        // but the backend keeps accepting turns on the unfinished session
        // until the maintenance window actually opens -- see
        // utils/maintenance.py on the backend. Gating on the flip alone would
        // hide a session the backend is still willing to serve, for however
        // long that gap runs (an hour or more, depending on the group's
        // cadence).
        //
        // turns_blocked alone is NOT the right signal for "is the old session
        // still current": it reads false both BEFORE the window opens (the
        // gap, old session still current) and AFTER it closes and releases
        // (summaries are ready, old session should now move to history and
        // "Begin a new session!" should render) -- two opposite answers for
        // the same value. new_session_blocked is what tells them apart: true
        // for exactly as long as summaries are not ready (spanning the gap
        // AND the window itself), false once they are. Held is therefore
        // "closed, and (turns are blocked right now OR summaries are not
        // ready yet)" -- true during the window and after release, false only
        // in the gap before it opens.
        //
        // null maintenance (feature off, or an older backend) falls back to
        // the pre-existing behaviour: undefined?.field is undefined, so
        // turns_blocked reads falsy and !new_session_blocked reads truthy,
        // collapsing this to plain newSessionNeeded.
        const heldByMaintenance = Boolean(
          newSessionNeeded &&
            (maintenance?.turns_blocked || !maintenance?.new_session_blocked)
        );

        if (!heldByMaintenance && remainingHistory.length > 0) {
          // Either the period hasn't closed, or it has but turns are still
          // open -- both show the most recent session as the live one.
          currentSessionId = remainingHistory[0]?.id || "";
          currentChatHistory = remainingHistory[0]?.history || [];
          previousChatHistory = remainingHistory.slice(1, 6); // Get next 5 previous sessions
        } else if (heldByMaintenance && remainingHistory.length > 0) {
          previousChatHistory = remainingHistory.slice(0, 5); // Get up to 5 previous sessions
        }
        // heldByMaintenance && remainingHistory.length === 0: new user, no
        // previous history -- both arrays stay empty, same as before.

        this.$patch({
          chatHistory: previousChatHistory,
          currentSessionId,
          currentChatHistory,
          newSessionNeeded,
          surveyLink,
          maintenance,
        });

        // Retrieve debug logs if admin
        if (this.rolesIncludes("admin")) {
          const logs = await debugApi.retrieveDebugLogs(
            currentSessionId,
            [],
            null,
            this.token
          );
          this.setDebugLogs(logs);
          console.log("Loaded debug logs for session", currentSessionId, logs);
        }
      } catch (error) {
        console.error("Error fetching chat history:", error);
      }
    },
    addCurrentChatHistoryEntry(entry) {
      this.currentChatHistory.push(entry);
      console.log(this.getCurrentChatHistory);
    },
    updateCurrentChatMessageText(messageId, nextText) {
      const idx = this.currentChatHistory.findIndex((m) => m.id === messageId);
      if (idx === -1) return;

      // safest: replace object reference to guarantee DOM update
      const prev = this.currentChatHistory[idx];
      this.currentChatHistory[idx] = { ...prev, text: nextText };
    },
    removeCurrentChatMessage(messageId) {
      // Used when a turn fails after assistant_started already placed an empty
      // bubble. The bubble is the coach's voice, so it must be taken away
      // rather than filled with an error.
      const idx = this.currentChatHistory.findIndex((m) => m.id === messageId);
      if (idx !== -1) this.currentChatHistory.splice(idx, 1);
    },
    removePendingUserMessage(wsMessageId) {
      // Take back a message the server never accepted, and hand its text back
      // so the composer can be refilled.
      //
      // Keyed on ws_message_id, not id: sendMessage stamps the optimistic user
      // entry with `id: Date.now()` and carries the turn's id separately, so
      // removeCurrentChatMessage above -- which matches on `id` -- would never
      // find it and the bubble would be stranded in the transcript.
      const idx = this.currentChatHistory.findIndex(
        (m) => m.ws_message_id === wsMessageId && !m.isResponse
      );
      if (idx === -1) return null;
      const [removed] = this.currentChatHistory.splice(idx, 1);
      return removed?.text || null;
    },
    upsertCurrentChatMessage(entry) {
      // optional helper: if exists, replace; else push
      const idx = this.currentChatHistory.findIndex((m) => m.id === entry.id);
      if (idx === -1) this.currentChatHistory.push(entry);
      else this.currentChatHistory[idx] = { ...this.currentChatHistory[idx], ...entry };
    },

    async fetchMentees() {
      try {
        const mentees = await mentorApi.getMentees(this.token);

        this.mentees = mentees;
        console.log("fetchMentees(): " + mentees);
      } catch (error) {
        console.error("Error fetching mentees:", error);
      }
    },
    async fetchPastSessionsWithSummaries() {
      try {
        const sessions = await mentorApi.getPastSessionsWithSummaries(
          this.token
        );
        this.pastSessionsWithSummaries = sessions;
        console.log("fetchPastSessionsWithSummaries(): " + sessions);
      } catch (error) {
        console.error(
          "Error fetching past chat sessions with summaries:",
          error
        );
      }
    },
    async fetchPastSessionsWithSummariesAndSuggestions() {
      try {
        const sessions =
          await mentorApi.getPastSessionsWithSummariesAndSuggestions(
            this.token
          );
        this.pastSessionsWithSummariesAndSuggestions = sessions;
        console.log(
          "fetchPastSessionsWithSummariesAndSuggestions(): " + sessions
        );
      } catch (error) {
        console.error(
          "Error fetching past chat sessions with summaries and suggestions:",
          error
        );
      }
    },
  },
});
